import { getCustomPanels, getOfficialBundleMappingRedis, getPromotionMapping, getAllTestsTubeInfo, getCustomPanelsWTubeInfo } from "@/api/va-order-page/orderTest";
import { getAllTestsAndDiscountPanelsApi, getCustomPanelsApi, getOfficialBundleMappingApi, getPromotionsApi } from "@/api/va-order-page/pricing/item/price";
import { getSampleTypeSetsNew } from "@/utils/va-order-test/sampleTypes";
import { getTestTree } from "../testTree/testTree";
import { bfsSearch } from "../testTree/bfsSearch";
import _ from "lodash";
import { getModifiedDiscountMap } from "@/store/modules/va-order-test/helpers";
import { getEligibleSeasonalPromos } from "@/store/modules/va-order-test/helpers/getSeasonalPromos";
import { getSampleTypesInPromo } from "./mjs/sampleTypes.mjs";
import { TreeNode } from "../testTree/TreeNode";
import { removeItem } from "../testAndPanelSelection";
import { getCachedOrderPageData, convertArraysToSets } from "../preloadService";
import { deserializeTreeNodes } from "../testTree/treeSerializer";

/** Find a test node by id or by value.originalId (for duplicate tests). Used by best-deal-extract mjs. */
export function getTestNode(testNodeList, id) {
  let node = testNodeList.find((n) => n.id === id);
  if (!node) {
    node = testNodeList.find((n) => n.value && n.value.originalId === id);
  }
  return node;
}

/**
 * get va and vw tests and discount panels
 * @returns { Object } - va vw test node list and discount panel list
 */
export async function getVaVwTestsAndDiscountPanels(isAllTestsWithTubeInfo, currencyName = "usd", isApi = false) {
  try {
    // Try to get cached data first
    const cachedData = getCachedOrderPageData(currencyName);
    
    // Normalize undefined to false for comparison
    const normalizedParam = isAllTestsWithTubeInfo || false;
    const normalizedCached = cachedData?.isAllTestsWithTubeInfo || false;
    const isMatch = normalizedParam === normalizedCached;
    
    if (cachedData && isMatch) {
      // log to indicate data source
      console.log(`[Order Page] Data source: sessionStorage cache for ${currencyName}`);
      // Deserialize tree structures
      const { rootNodeList: vaRootNodeList, nodeList: vaNodeList } = deserializeTreeNodes(cachedData.vaTreeData);
      const { rootNodeList: vwRootNodeList, nodeList: vwNodeList } = deserializeTreeNodes(cachedData.vwTreeData);
      
      // Convert sampleTypeSets arrays back to Sets
      vaNodeList.forEach(item => {
        if (item.value?.sampleTypeSets) {
          item.value.sampleTypeSets = convertArraysToSets(item.value.sampleTypeSets);
        }
      });
      
      vwNodeList.forEach(item => {
        if (item.value?.sampleTypeSets) {
          item.value.sampleTypeSets = convertArraysToSets(item.value.sampleTypeSets);
        }
      });
      
      cachedData.discountPanelList.forEach(panel => {
        if (panel.sampleTypeSets) {
          panel.sampleTypeSets = convertArraysToSets(panel.sampleTypeSets);
        }
      });
      
      // Handle duplicate tests
      addDuplicateTests(vaNodeList, vwNodeList);
      
      return {
        vaRootNodeList,
        vaNodeList,
        vwRootNodeList,
        vwNodeList,
        discountPanelList: cachedData.discountPanelList,
        exceptions: cachedData.exceptions,
        discountPanelCategory: cachedData.discountPanelCategory,
        vwTestCategory: cachedData.vwTestCategory,
        dbsMappings: cachedData.dbsMappings,
        regenere: cachedData.regenere || [],
        nutriproz: cachedData.nutriproz || []
      };
    }
    
    // If no cache, fetch from API
    console.log(`[Order Page] Data source: API for ${currencyName}`);
    const { data } = await getAllTestsAndDiscountPanelsApi(currencyName, isAllTestsWithTubeInfo);
    const dbsMappings = data.atHomeMappings || []; 
    let { vaTests, vwTests, discountPanels, exceptions, dpCategory, vwTestCategory, regenere, nutriproZ } = data;
    // Handle undefined: set to empty array if backend not given
    regenere = regenere || [];
    let nutriproz = nutriproZ || [];
    vaTests.forEach((test) => {
      test.isSelect = false;
      test.isSelectForShortcut = false;
      test.isSelectForFavorite = false;
      test.category = "va";
      test.isSelectForXChooseY = false;
      test.isNotAllowedSelect = false;
      test.isNotAllowedSelectForShortcut = false;
      test.isNotAllowedSelectForFavorite = false;
      test.isNotAllowedSelectForXChooseY = false;
      test.isException = false;
      test.sampleTypeSets = getSampleTypeSetsNew(test);
      test.originalTestName = test.testName;
      test.testName = test.displayName ? test.displayName : test.testName;
      test.originalPrice = test.price;
    });
    // create a tree strcture for va tests
    let vaTestTree = getTestTree(vaTests);
    //order the root list
    let order = [1, 6, 18, 21, 22, 99, 198, 728, 14, 98, 23, 24, 199, 201, 65, 727, 51, 118, 7, 732, 710, 202];
    let vaRootNodeList = _.sortBy(vaTestTree, function(node) {
      return _.indexOf(order, node.id);
    });
    // get a BFS search order array from the tree structure
    let vaNodeList = [];
    vaRootNodeList.forEach(root => {
      bfsSearch(root, vaNodeList, 0);
    });
    // vw Tests
    vwTests.forEach((test) => {
      test.isSelect = false;
      test.isSelectForShortcut = false;
      test.isSelectForFavorite = false;
      test.category = "vw";
      test.isSelectForXChooseY = false;
      test.isNotAllowedSelect = false;
      test.isNotAllowedSelectForShortcut = false;
      test.isNotAllowedSelectForFavorite = false;
      test.isNotAllowedSelectForXChooseY = false;
      test.isException = false;
      test.sampleTypeSets = getSampleTypeSetsNew(test);
      test.originalTestName = test.testName;
      test.testName = test.displayName ? test.displayName : test.testName;
      test.originalPrice = test.price;
    });
    let vwRootNodeList = getTestTree(vwTests);
    let vwNodeList = [];
    vwRootNodeList.forEach(root => {
      bfsSearch(root, vwNodeList, 0);
    });
    // handle for any duplicate tests
    addDuplicateTests(vaNodeList, vwNodeList);
    // discount panels
    discountPanels.forEach(panel => {
      panel.isSelect = false;
      panel.testName = panel.bundleName;
      panel.isSelectForShortcut = false;
      panel.category = panel.quantity ? "xChooseY" : "discountPanel";
      panel.isException = false;
      panel.sampleTypeSets = getSampleTypeSetsNew(panel);
      if (panel.quantity) {
        panel.isOpenXChooseYDialog = false;
      }
      let { testList, packageList } = panel.bundleContent;
      let testIdList = testList ? testList.map(t => t.testId) : [];
      let packageIdList = packageList ? packageList.map(p => p.packageId) : [];
      panel.testIdsInPanel = [...new Set(testIdList.concat(packageIdList))];
      // add supplement test ids in testIdsInPanel for xChooseY panel
      if (panel.quantity && panel.supplementContent) {
        let { itemIdList } = panel.supplementContent;
        panel.testIdsInPanel = [...new Set(panel.testIdsInPanel.concat(itemIdList))];
      }
    });
    return { 
      vaRootNodeList, 
      vaNodeList, 
      vwRootNodeList, 
      vwNodeList, 
      discountPanelList: discountPanels,
      exceptions,
      discountPanelCategory: dpCategory,
      vwTestCategory,
      dbsMappings,
      regenere,
      nutriproz
    };
  } catch (err) {
    console.log("getVaVwTestsAndDiscountPanels err", err);
    return {
      vaRootNodeList: [], 
      vaNodeList: [], 
      vwRootNodeList: [], 
      vwNodeList: [], 
      discountPanelList: [],
      exceptions:[],
      discountPanelCategory: [],
      vwTestCategory: [],
      dbsMappings: [],
      regenere: [],
      nutriproz: []
    }
  }
  
}

export async function getCustomPanelList(payload) {
  let { currency, clinicId, customerId, accountType } = payload
  const { data } = await getCustomPanelsApi(currency, clinicId, customerId, accountType);
  let customPanelList = data ? data : [];
  // add isSelect property to each panel
  customPanelList.forEach((panel) => {
    panel.isSelect = false;
    panel.testName = panel.bundleName;
    panel.category = "customPanel";
    panel.selectedXChooseYPanelList = [];
    panel.isSelectForShortcut = false;
    panel.sampleTypeSets = getSampleTypeSetsNew(panel);
  });
  return customPanelList;
}

export async function getCustomPanelListWTubeInfos() {
  const { data } = await getCustomPanelsWTubeInfo();
  let customPanelList = data;
  customPanelList.forEach((panel) => {
    panel.isSelect = false;
    panel.testName = panel.bundleName;
    panel.category = "customPanel";
    panel.selectedXChooseYPanelList = [];
    panel.isSelectForShortcut = false;
    panel.sampleTypeSets = getSampleTypeSetsNew(panel);
  });
  return customPanelList;
}

export async function getDiscountMapAndSeasonalPromotionList(discountPanelList, testNodeList) {
  let responses = await Promise.allSettled([
    getOfficialBundleMappingApi(), 
    getPromotionsApi(),
    getPromotionMapping()
  ]);
  let [discountMapResp, promotionListResp, promotionMapResp] = responses;
  let discountMap = discountMapResp.status === "fulfilled" ? discountMapResp.value.data : [];
  let promotionList = promotionListResp.status === "fulfilled" ? promotionListResp.value.data : [];
  let promotionMap = promotionMapResp.status === "fulfilled" ? promotionMapResp.value.data : [];
  let testList = testNodeList.map(node => node.value);
  let eligibleSeasonalPromos = 
    getEligibleSeasonalPromos(
      promotionList, 
      promotionMap, 
      discountPanelList, 
      testList
    );
  // add sample sets
  if (!eligibleSeasonalPromos) {
    eligibleSeasonalPromos = [];
  }
  eligibleSeasonalPromos.forEach(promo => {
    promo.sampleTypeSets = getSampleTypesInPromo(promo, testList);
  });
  let modifiedDiscountMap = getModifiedDiscountMap(eligibleSeasonalPromos, discountMap, testNodeList);
  return { discountMap: modifiedDiscountMap, seasonalPromotionList: eligibleSeasonalPromos };
}

export function handleTestsAndBundlesFromPayload(payload) {
  let { test_id_list, discount_panel_id_list, x_choose_y_list, promo_list } = payload;
  let testIds = [];
  let discountPanelIds = [];
  testIds = test_id_list ? test_id_list: [];
  discountPanelIds = discount_panel_id_list ? discount_panel_id_list : [];
  if (x_choose_y_list) {
    x_choose_y_list.forEach(xChooseY => {
      let xChooseYTestIds = xChooseY && xChooseY.test_id_list ? xChooseY.test_id_list : [];
      testIds = testIds.concat(xChooseYTestIds);
    })
  }
  if (promo_list) {
    promo_list.forEach(promo => {
      let { test_id_list, discount_panel_id_list, x_choose_y_list } = promo;
      let promoTests = test_id_list ? test_id_list : [];
      if (x_choose_y_list) {
        x_choose_y_list.forEach(xChooseY => {
          let xChooseYTestIds = xChooseY && xChooseY.test_id_list ? xChooseY.test_id_list : [];
            promoTests = promoTests.concat(xChooseYTestIds);
        });
      }
      let promoDiscuntPanels = discount_panel_id_list ? discount_panel_id_list : [];
      testIds = testIds.concat(promoTests);
      discountPanelIds = discountPanelIds.concat(promoDiscuntPanels);
    });
  }
  return { 
    testIds: testIds.map(id => id.includes("_") ? id : Number(id)), 
    discountPanelIds: discountPanelIds.map(id => Number(id))
  };
}

export function parseBestDealSuggestionObj(bestDealSuggestion) {
  let suggest_bundle_list = [];
  bestDealSuggestion.suggestBundleList.forEach(s => {
    let suggest_bundle = {
      id: s.appliedBundleId.toString(),
      price: s.bundlePrice.toString(),
      category: s.category,
      zoomers : s.appliedZoomers ? s.appliedZoomers.map(id => id.toString()) : null,
      supplements : s.appliedSupplement ? s.appliedSupplement.map(id => id.toString()) : null
    };
    suggest_bundle_list.push(suggest_bundle);
  })
  let result = {
    suggest_bundle_list,
    left_over_test_id_list: bestDealSuggestion.leftOverTestIdList.map(id => id.toString()),
    best_deal_price: bestDealSuggestion.bestDealPrice
  }
  return result;
}

function addDuplicateTests(vaTestNodeList, vwTestNodeList) {
  let testNodeList = vaTestNodeList.concat(vwTestNodeList);
  let duplicateTestNodes = testNodeList.filter(n => n.value.duplicateTestPackages && n.value.duplicateTestPackages.length > 0);
  duplicateTestNodes.forEach(node => {
    // determine target list based on original category
    const targetList = node.value.category === 'vw' ? vwTestNodeList : vaTestNodeList;
    // add the duplicate node to each of its parent
    node.value.duplicateTestPackages.forEach(parentId => {
      let parentNode = testNodeList.find(n => n.id === parentId);
      // create a copy of node.value and create a new TreeNode
      let newNodeValue = { ...node.value };
      newNodeValue.originalId = node.id;
      newNodeValue.id = [node.id, parentId].join("_");
      newNodeValue.toBeOffline = parentNode.value.toBeOffline ? true : false;
      let duplicateTreeNode = new TreeNode(newNodeValue);
      // add link between this copy and its parent
      duplicateTreeNode.parent = parentNode;
      if (!parentNode) {
        return;
      }
      duplicateTreeNode.level = parentNode.level + 1;
      removeItem(node, parentNode.descendants);
      parentNode.descendants.push(duplicateTreeNode);
      // add to target list based on category
      targetList.push(duplicateTreeNode);
    });
    // remove the original duplicate test from testNodeList and from its parent (if any)
    if (node.parent) {
      removeItem(node, node.parent.descendants);
      node.parent = null;
    }
    removeItem(node, targetList);
  })
}